/**
 * Outstanding provisioning claims for one Access node class (design §10.5).
 *
 * Server-paginated (VSNP-1094) via the shared DataTable, filtered to this
 * device class so each AGW/ACN page pages independently. Tokens arrive MASKED
 * from the list endpoint — the full token only ever existed in the wizard's
 * step 2 (the display-once guarantee).
 */

import { useCallback, useEffect, useState } from 'react';
import { KeyRound, XCircle } from 'lucide-react';
import DataTable, { type Column } from '../common/DataTable';
import StatusBadge from '../common/StatusBadge';
import ConfirmDialog from '../common/ConfirmDialog';
import { useToast } from '../common/ToastContainer';
import accessService, { type ProvisionClaim } from '../../services/access.service';

const PAGE_SIZE = 10;

function expiryOf(claim: ProvisionClaim): string {
  if (claim.status !== 'active') return '—';
  const ms = new Date(claim.expires_at).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const days = Math.floor(ms / 86_400_000);
  if (days >= 1) return `${days}d`;
  return `${Math.max(1, Math.floor(ms / 3_600_000))}h`;
}

interface ClaimsPanelProps {
  deviceType: 'gateway' | 'app_connector';
  /** node row id → display name, so a claim can say what it is for. */
  nodeNames: Record<string, string>;
  /** Bump to make the panel refetch (e.g. after the wizard minted a claim). */
  refreshKey?: number;
}

const ClaimsPanel = ({ deviceType, nodeNames, refreshKey = 0 }: ClaimsPanelProps) => {
  const toast = useToast();
  const [claims, setClaims] = useState<ProvisionClaim[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ProvisionClaim | null>(null);
  const [revoking, setRevoking] = useState(false);

  const fetchClaims = useCallback(async (p: number) => {
    try {
      setLoading(true);
      const result = await accessService.listNodeClaims({
        deviceType,
        limit: PAGE_SIZE,
        offset: (p - 1) * PAGE_SIZE,
      });
      setClaims(result.items);
      setTotal(result.total);
    } catch {
      // Non-fatal: the node list above is the primary surface.
      setClaims([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [deviceType]);

  useEffect(() => {
    void fetchClaims(page);
  }, [fetchClaims, page, refreshKey]);

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    try {
      setRevoking(true);
      await accessService.revokeNodeClaim(revokeTarget.id);
      toast.success('Token revoked', revokeTarget.token);
      setRevokeTarget(null);
      await fetchClaims(page);
    } catch (err) {
      toast.error('Revoke failed', err instanceof Error ? err.message : 'Could not revoke the token');
    } finally {
      setRevoking(false);
    }
  };

  // Nothing to show and nothing loading → hide the panel entirely.
  if (total === 0 && claims.length === 0 && !loading) return null;

  const columns: Column<ProvisionClaim>[] = [
    { key: 'token', label: 'Token', render: (c) => <span className="font-mono text-xs text-gray-700">{c.token}</span> },
    {
      key: 'for',
      label: 'For',
      render: (c) =>
        c.device_id ? (
          <span className="text-gray-700">{nodeNames[c.device_id] ?? `${c.device_id.slice(0, 8)}…`}</span>
        ) : (
          <span className="text-gray-400">unlinked</span>
        ),
    },
    { key: 'status', label: 'Status', render: (c) => <StatusBadge status={c.status === 'claimed' ? 'used' : c.status} /> },
    { key: 'expires', label: 'Expires in', render: (c) => <span className="text-gray-600">{expiryOf(c)}</span> },
    {
      key: 'used_by',
      label: 'Used by',
      render: (c) => <span className="font-mono text-xs text-gray-500">{c.claimed_by_guid ?? '—'}</span>,
    },
    {
      key: 'actions',
      label: '',
      render: (c) =>
        c.status === 'active' ? (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-danger-600"
            title="Revoke this token"
            onClick={() => setRevokeTarget(c)}
          >
            <XCircle className="h-3.5 w-3.5" /> Revoke
          </button>
        ) : null,
    },
  ];

  return (
    <div className="card">
      <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
        <KeyRound className="h-4 w-4 text-teal-600" /> Registration tokens
        <span className="text-xs font-normal text-gray-400">{total} total</span>
      </h2>
      <DataTable
        columns={columns}
        data={claims}
        loading={loading}
        searchable={false}
        pageSize={PAGE_SIZE}
        page={page}
        totalItems={total}
        totalPages={Math.max(1, Math.ceil(total / PAGE_SIZE))}
        onPageChange={setPage}
        emptyMessage="No registration tokens"
      />

      <ConfirmDialog
        isOpen={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onConfirm={handleRevoke}
        title="Revoke registration token"
        message={`Revoke ${revokeTarget?.token}? A node holding it will no longer be able to enrol. Mint a new token from the node's page if needed.`}
        confirmText="Revoke"
        variant="danger"
        isLoading={revoking}
      />
    </div>
  );
};

export default ClaimsPanel;
